/**
 * The web design tokens. Imported HERE because it was imported NOWHERE.
 *
 * `constants/theme.ts` resolves every web font to a CSS custom property —
 * `Fonts.sans` is `var(--font-display)`, `Fonts.serif` is `var(--font-serif)`
 * — and `global.css` is the only file that defines them. Without this import
 * those variables never existed, so every `fontFamily` on web was an invalid
 * declaration that the browser dropped, and the whole web app fell back to a
 * default face. Found while tracing the poster export, but it was never
 * confined to the poster.
 *
 * Safe on native, verified in the installed source rather than assumed:
 * `@expo/metro-config`'s `transformCss` returns an EMPTY MODULE for any
 * platform that is not web, so this costs iOS and Android nothing at all.
 */
import '../global.css';

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  AppState,
  Platform,
  StyleSheet,
  useWindowDimensions,
} from 'react-native';

import { onForeground } from '@/lib/foreground';
import {
  DefaultTheme,
  Stack,
  ThemeProvider,
  useRouter,
  type ErrorBoundaryProps,
  type Href,
} from 'expo-router';
import * as NativeSplashScreen from 'expo-splash-screen';
import * as WebBrowser from 'expo-web-browser';
import { StatusBar } from 'expo-status-bar';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { useTranslation } from 'react-i18next';
import { KeyboardProvider } from 'react-native-keyboard-controller';
import { SafeAreaProvider } from 'react-native-safe-area-context';

import { referralsApi } from '@/api';
import { ConfirmDialog } from '@/components/confirm-dialog';
import { ErrorFallback } from '@/components/ui/error-fallback';
import { SplashView } from '@/components/ui/splash-view';
import { MAX_FONT_SCALE } from '@/components/themed-text';
import {
  UpdateBanner,
  UpdateRequiredScreen,
} from '@/components/ui/update-gate';
import { useUpdateGate } from '@/hooks/use-update-gate';
import { initTelemetry } from '@/lib/telemetry-sentry';
import { useAuthRouting } from '@/hooks/use-auth-routing';
import { useLocationAccessWatch } from '@/hooks/use-location-access-watch';
import i18n, { initI18n } from '@/i18n';
import {
  getInitialNotificationRoute,
  subscribeToNotificationResponses,
} from '@/lib/notifications';
import { BRAND_NAME, WEB_TITLE } from '@/constants/brand';
import { initWebNotificationFallback } from '@/lib/web-notifications';
import { retryUnsettledHide } from '@/state/session-store';
import { useAuthStore } from '@/state/auth-store';
import { installKeyboardBackgroundReset } from '@/components/ui/keyboard-view';
import { installOutboxTriggers } from '@/state/outbox-store';
import {
  AppThemeProvider,
  useAppTheme,
  useResolvedAppColors,
} from '@/theme/app-theme';

// Crash reporting, before anything else runs, so a failure during startup is
// captured rather than lost. A no-op unless this environment's config enables
// it AND carries a DSN — see docs/observability/SENTRY_DATA_POLICY.md.
//
// At module scope rather than in an effect: an error thrown while the first
// render is still mounting happens before any effect fires, and that is exactly
// the crash worth having.
initTelemetry();

// Hand an OAuth popup's result back to the window that opened it.
//
// Here, at the ROOT, and not in the screen that starts the flow — which is
// where it used to live, and why Google sign-in still failed after the COOP
// header was fixed.
//
// On web the redirect URI is the bare origin (`makeRedirectUri` →
// `new URL('', window.location.origin)`), so Google returns the popup to `/`,
// not to `/welcome`. `welcome.tsx` therefore may not even be evaluated yet —
// and by the time it is, the router has moved the popup to `/welcome`.
// `maybeCompleteAuthSession` compares the CURRENT url against the redirect url
// it stored (origin + pathname, via `normalizeUrl`), so it then sees
// `…/welcome` against `…/` , decides they do not match, and returns
// `{ type: 'failed' }` — without posting anything and without throwing.
//
// The symptom is the popup sitting on the sign-in screen while the original tab
// waits forever, which is indistinguishable from the COOP failure it followed.
// Running at the root means this happens on whatever URL the popup lands on,
// before any routing can move it.
WebBrowser.maybeCompleteAuthSession();

void NativeSplashScreen.preventAutoHideAsync();
// No fade. The JS screen underneath is the same picture on the same background,
// so there is nothing to dissolve *to* — all a cross-fade does is spend 180ms
// showing the two blended, which reads as the mark dimming and brightening
// again on the way in. Cut straight from one to the other and nothing appears
// to happen at all.
NativeSplashScreen.setOptions({ fade: false });

let splashHideInFlight = false;

/**
 * Hand over from the native splash to React.
 *
 * Called from the FIRST thing this tree paints — including the placeholder that
 * stands in while i18n resolves. It is intentionally safe to call more than
 * once: Android can show the native splash again when an Activity is recreated
 * while the JS runtime survives, for example after locking and reopening the
 * phone. A "hide only once per JS process" guard leaves that second native
 * splash stuck above the app.
 */
function hideNativeSplash(): void {
  if (splashHideInFlight) return;
  splashHideInFlight = true;
  void NativeSplashScreen.hideAsync()
    .catch((err) => {
      console.warn(
        '[splash] unable to hide native splash',
        err,
      );
    })
    .finally(() => {
      splashHideInFlight = false;
    });
}

function RootNavigator() {
  const { colors } = useAppTheme();
  const router = useRouter();
  // Subscribed, not read once: the tab-title effect below has to re-run when
  // the language changes, and `i18n.language` alone would not re-render.
  const { i18n: activeI18n } = useTranslation();

  const status = useAuthStore(
    (state) => state.status,
  );

  const hydrate = useAuthStore(
    (state) => state.hydrate,
  );

  useEffect(() => {
    void hydrate();
  }, [hydrate]);

  // The message outbox flushes on reconnect and on returning to the
  // foreground, so its triggers belong to the app rather than to any screen —
  // a queued message must keep trying while its thread is closed.
  useEffect(() => installOutboxTriggers(), []);

  // The keyboard has to be let go of when the app backgrounds, or it comes back
  // with its space reserved and no keyboard in it. App-wide for the same reason
  // as the outbox: every screen with a composer shares one keyboard.
  useEffect(() => installKeyboardBackgroundReset(), []);

  useEffect(() => {
    const sub = AppState.addEventListener(
      'change',
      (next) => {
        if (next === 'active') {
          hideNativeSplash();
        }
      },
    );

    return () => sub.remove();
  }, []);

  useAuthRouting();

  // The launch trigger (ADR-029): one unauthenticated call for flags, the
  // store-update gate, and the OTA instruction, refreshed on foreground.
  // Fails open — the initial state shows nothing and errors keep it that way.
  const updateGate = useUpdateGate();

  // Only once signed in: checking asks the browser for a real position, which
  // is not something to do behind the login screen.
  useLocationAccessWatch(status === 'authenticated');

  // Web-only in-page fallback: play a soft sound / toast / title badge when a
  // push arrives, in case the browser silently drops the system notification.
  useEffect(() => {
    initWebNotificationFallback();
  }, []);

  /**
   * The tab title, in the reader's language.
   *
   * `+html.tsx` writes an English `<title>` into the static shell, and that
   * cannot be otherwise: one HTML file is exported and served to everyone,
   * before any language is known. So the shell carries the default and this
   * replaces it once i18n has resolved — which is how a localized tab title
   * works on a static export, and why `WEB_TITLE` stays as the English
   * fallback rather than being deleted.
   *
   * It also still fixes the original bug this effect existed for: react-helmet
   * mounts its own title element and, with no route supplying text, emits it
   * EMPTY before the shell's. Two titles in a document and the browser takes
   * the first, so the tab reads as untitled. The static export carries one
   * title and is unaffected; the dev server is where it was reported.
   *
   * Three things it must not break:
   *   - A route that sets its own title with `expo-router/head` still wins.
   *     We only write over an empty title or over one WE last wrote.
   *   - The unread badge. `web-notifications.ts` prefixes "(3) " and reads it
   *     back with `stripTitleBadge`, so the prefix is carried across rather
   *     than clobbered on a language change.
   *   - `common.*` lives in the ROOT bundle, not a lazy namespace, so this
   *     does not wait on a chunk that may never load on this route.
   */
  const ownedTitleRef = useRef<string | null>(null);
  useEffect(() => {
    // No readiness check: `RootLayoutContent` renders this tree only once
    // `initI18n` has resolved, so `t` is live by the time this runs.
    if (Platform.OS !== 'web' || typeof document === 'undefined') return;

    const badge = /^\(\d+\)\s*/.exec(document.title)?.[0] ?? '';
    const current = document.title.slice(badge.length);
    // Ours to replace: nothing, the shell's English default, or the value we
    // wrote last time. Anything else belongs to a route and is left alone.
    const owned =
      !current ||
      current === WEB_TITLE ||
      current === ownedTitleRef.current;
    if (!owned) return;

    // i18next returns the KEY when a bundle is missing, which would put
    // "common.webTitle" in the tab. Fall back to the English default instead.
    const key = 'common.webTitle';
    const translated = i18n.t(key, { brand: BRAND_NAME });
    const next = translated && translated !== key ? translated : WEB_TITLE;

    ownedTitleRef.current = next;
    document.title = `${badge}${next}`;
  }, [activeI18n.language]);

  // Referral retention heartbeat: ping on launch and each foreground so the
  // server can count distinct active days. Deduped per-day server-side, and a
  // best-effort no-op when signed out.
  useEffect(() => {
    if (status !== 'authenticated') return undefined;
    void referralsApi.appOpen();
    // Rate limited: on web a foreground is a tab flip, and this ping is
    // deduped per DAY server-side, so a second one within the minute cannot
    // tell the server anything it does not already know.
    return onForeground(
      'referral.appOpen',
      () => {
        void referralsApi.appOpen();
      },
      // The ping immediately above IS this job's first run.
      { justRan: true },
    );
  }, [status]);

  // Plan state changes off-device — a Stripe webhook, a referral grant
  // vesting, a store event — and until this listener existed, nothing told a
  // RUNNING app: `refreshUser` was only called by the purchase screens, so a
  // server-side grant stayed invisible until the next cold launch (`hydrate`
  // refetches; foreground did not). One re-read per foreground keeps every
  // `user.plan` gate honest, and it covers ALL grant sources because the
  // server derives `plan` per read (`effectivePlan`, grants included).
  // Best-effort: `refreshUser` throws on network failure, and a foreground
  // with no connectivity is routine, not reportable.
  useEffect(() => {
    if (status !== 'authenticated') return undefined;
    const refresh = () => {
      useAuthStore
        .getState()
        .refreshUser()
        .catch(() => {});
    };
    // The plan re-read is rate limited — a Stripe webhook or a vesting grant
    // does not land in the second between two tab flips, and this was one of
    // the four foreground handlers that each cost a request.
    //
    // No `justRan`, deliberately: unlike the two jobs above, nothing reads the
    // plan beside this subscription. `hydrate` covers a cold launch, but this
    // effect also re-runs when `status` becomes authenticated, and claiming a
    // run here would suppress the first real foreground after a sign-in.
    const stopRefresh = onForeground('auth.refreshUser', refresh);
    // NOT rate limited, and deliberately so: a hide the server never received
    // is a correctness repair, not a read. The switch already says hidden and
    // the retry list ran out while the phone had no usable connection; coming
    // back is the moment that has plausibly changed. It is a no-op when there
    // is nothing outstanding, so it costs nothing to run on every foreground.
    const sub = AppState.addEventListener('change', (next) => {
      if (next === 'active') retryUnsettledHide();
    });
    return () => {
      stopRefresh();
      sub.remove();
    };
  }, [status]);

  // Deep-link notification taps to the relevant screen. Only navigate once the
  // user is authenticated so we never push a protected route (e.g. a chat)
  // onto the auth/onboarding flow.
  useEffect(() => {
    if (status !== 'authenticated') {
      return undefined;
    }

    let active = true;
    let unsubscribe = () => {};

    void (async () => {
      const initialRoute = await getInitialNotificationRoute();
      if (active && initialRoute) {
        router.push(initialRoute as Href);
      }

      unsubscribe = await subscribeToNotificationResponses((route) => {
        router.push(route as Href);
      });

      if (!active) unsubscribe();
    })();

    return () => {
      active = false;
      unsubscribe();
    };
  }, [status, router]);

  if (status === 'loading') {
    return <SplashView onLayout={hideNativeSplash} />;
  }

  // The server retired this build. Only reachable after a successful,
  // validated answer — never from an error — so it fails open by construction.
  // Takes over from the splash the same way a route would, or Android would
  // leave the native splash covering it forever.
  if (updateGate.blocked) {
    return <UpdateRequiredScreen onLayout={hideNativeSplash} />;
  }

  return (
    <>
      <Stack
        screenOptions={{
          headerShown: false,
          animation: 'fade',
          contentStyle: {
            backgroundColor:
              colors.background,
          },
        }}>
      <Stack.Screen name="(auth)" />

      <Stack.Screen name="(onboarding)" />

      <Stack.Screen name="(tabs)" />

      {/*
        ONE presentation, not two. This route used to be a
        `transparentModal` with `animation: 'none'`, whose body was
        itself an RN `<Modal animationType="slide">` - so opening
        the dashboard presented it twice and closing dismissed it
        twice. On iOS each `Modal` is a real UIViewController
        presentation, which is why both directions dragged there
        and far less on Android, where a Modal is a Dialog.

        The slide now belongs to the route, and the inner Modal is
        gone (`growth-dashboard-modal.tsx`).
      */}
      <Stack.Screen
        name="dashboard"
        options={{
          headerShown: false,
          // INSTANT, deliberately. Build 12 was slow to open and close because
          // this route is a modal whose body was ALSO an RN `<Modal>` — two
          // presentations, two dismissals. Removing the inner one fixed
          // closing outright, and `slide_from_bottom` was added on the theory
          // that the open needed an animation to feel deliberate.
          //
          // Build 14 measured what that theory rested on: mounting the whole
          // dashboard body takes ~10ms (`dashboard.route.mount` to
          // `body.painted`), so nothing was blocking the slide — the slide
          // itself was the jank. The owner's call, and the right one: Goals and
          // Reflections open instantly as plain modals and feel better for it.
          // An instant appearance cannot stutter.
          animation: 'none',
          presentation: 'transparentModal',
          contentStyle: {
            backgroundColor:
              colors.background,
          },
        }}
      />

      <Stack.Screen
        name="pricing"
        options={{
          headerShown: false,
          animation: 'slide_from_right',
          presentation: 'card',
          contentStyle: {
            backgroundColor:
              colors.background,
          },
        }}
      />

      <Stack.Screen
        name="checkout"
        options={{
          headerShown: false,
          animation: 'fade',
          presentation: 'card',
          contentStyle: {
            backgroundColor:
              colors.background,
          },
        }}
      />

      <Stack.Screen
        name="subscription"
        options={{
          headerShown: false,
          animation: 'slide_from_right',
          presentation: 'card',
          contentStyle: {
            backgroundColor:
              colors.background,
          },
        }}
      />

      <Stack.Screen
        name="subscribe"
        options={{
          headerShown: false,
          animation: 'slide_from_right',
          presentation: 'card',
          contentStyle: {
            backgroundColor:
              colors.background,
          },
        }}
      />

      <Stack.Screen
        name="invite"
        options={{
          headerShown: false,
          animation: 'slide_from_right',
          presentation: 'card',
          contentStyle: {
            backgroundColor:
              colors.background,
          },
        }}
      />

      <Stack.Screen
        name="chat/[id]"
        options={{
          headerShown: false,
          animation: 'slide_from_right',
          presentation: 'card',
          contentStyle: {
            backgroundColor:
              colors.background,
          },
        }}
      />

      <Stack.Screen
        name="practice/index"
        options={{
          headerShown: false,
          animation: 'slide_from_right',
          presentation: 'card',
          contentStyle: {
            backgroundColor:
              colors.background,
          },
        }}
      />

      <Stack.Screen
        name="practice/[id]"
        options={{
          headerShown: false,
          animation: 'slide_from_right',
          presentation: 'card',
          contentStyle: {
            backgroundColor:
              colors.background,
          },
        }}
      />

      <Stack.Screen
        name="legal/terms-of-service"
        options={{
          headerShown: false,
          presentation: 'card',
        }}
      />

      <Stack.Screen
        name="legal/privacy-policy"
        options={{
          headerShown: false,
          presentation: 'card',
        }}
      />

      <Stack.Screen
        name="help/index"
        options={{
          headerShown: false,
          animation: 'slide_from_right',
          presentation: 'card',
        }}
      />

      <Stack.Screen
        name="help/topics"
        options={{ headerShown: false, presentation: 'card' }}
      />

      <Stack.Screen
        name="help/staying-safe"
        options={{ headerShown: false, presentation: 'card' }}
      />

      <Stack.Screen
        name="help/report-problem"
        options={{ headerShown: false, presentation: 'card' }}
      />

      <Stack.Screen
        name="help/feedback"
        options={{ headerShown: false, presentation: 'card' }}
      />

      <Stack.Screen
        name="help/contact"
        options={{ headerShown: false, presentation: 'card' }}
      />

      <Stack.Screen
        name="help/community-guidelines"
        options={{ headerShown: false, presentation: 'card' }}
      />

      <Stack.Screen
        name="help/privacy-account"
        options={{ headerShown: false, presentation: 'card' }}
      />

      <Stack.Screen
        name="help/billing-plus"
        options={{ headerShown: false, presentation: 'card' }}
      />
      </Stack>

      {/* Dismissible "update available" prompt; dismissal is remembered per
          installed build, so it is a prompt and not a nag. */}
      {updateGate.prompt ? (
        <UpdateBanner onDismiss={updateGate.dismiss} />
      ) : null}
    </>
  );
}

function RootLayoutContent() {
  const {
    colors,
    isDark,
    statusBarStyle,
  } = useAppTheme();

  const navigationTheme = useMemo(
    () => ({
      ...DefaultTheme,
      dark: isDark,
      colors: {
        ...DefaultTheme.colors,
        primary: colors.primary,
        background: colors.background,
        card: colors.surface,
        text: colors.text,
        border: colors.border,
        notification: colors.primary,
      },
    }),
    [colors, isDark],
  );

  return (
    <ThemeProvider value={navigationTheme}>
      <StatusBar
        style={statusBarStyle}
      />

      <RootNavigator />

      {/*
        Mounted at the ROOT, not inside (tabs). `confirm()` is called from chat
        and group screens too, and a dialog host that only exists under the tab
        navigator would leave those callers awaiting a promise nothing can
        settle — the same shape as the Alert bug it replaces, just quieter.
      */}
      <ConfirmDialog />
    </ThemeProvider>
  );
}

/**
 * The last boundary in the application.
 *
 * Anything a route or a nested layout does not catch arrives here. Until this
 * existed, the only `ErrorBoundary` in the app was on Profile, so a render
 * failure anywhere else left a blank screen that force-quitting was the only
 * exit from.
 *
 * `ErrorFallback` deliberately touches no app context — the tree it would read
 * from is the tree that may have just failed. See the note in that file.
 */
export function ErrorBoundary({ error, retry }: ErrorBoundaryProps) {
  return <ErrorFallback error={error} retry={retry} scope="root" />;
}

export default function RootLayout() {
  // Resolved here rather than via `useAppTheme()` because this component sits
  // ABOVE `AppThemeProvider` — and the root view is exactly the thing that has
  // to be painted before the provider exists.
  const rootColors = useResolvedAppColors();

  // The web half of the same fix the root view below applies to Android's
  // window background: paint the thing UNDER the app so it can never be seen.
  //
  // On web that thing is `<body>`, which is white by browser default. The app
  // fills the layout viewport, but a phone browser's visual viewport is
  // taller — the home-indicator strip below the tab bar, and the overscroll
  // area — and both showed a white band under the navy tab bar, in the dark
  // theme especially.
  //
  // Painted from `rootColors`, the SAME resolved palette the root view uses,
  // so body and app cannot disagree; re-runs on theme change, so the toggle
  // takes the strip with it. Deliberately NOT a CSS media query or a
  // `prefers-color-scheme` rule in `global.css`: the theme here can be an
  // explicit in-app preference that overrides the system one, and a stylesheet
  // cannot see that preference.
  useEffect(() => {
    if (Platform.OS !== 'web' || typeof document === 'undefined') return;
    const { documentElement, body } = document;
    // `html` as well as `body`: iOS Safari paints the overscroll area from the
    // root element, not from body.
    documentElement.style.backgroundColor = rootColors.background;
    body.style.backgroundColor = rootColors.background;
  }, [rootColors.background]);

  // See the note on the key below. `useWindowDimensions` re-renders on the
  // change because RCTDeviceInfo observes
  // RCTAccessibilityManagerDidUpdateMultiplierNotification and re-emits
  // dimensions (RCTDeviceInfo.mm:88,150), so this value is live rather than
  // read once at launch.
  const { fontScale } = useWindowDimensions();
  const effectiveFontScale = Math.min(fontScale, MAX_FONT_SCALE);

  // Resolve the saved/device language before rendering any translated UI.
  const [i18nReady, setI18nReady] = useState(false);

  useEffect(() => {
    void initI18n()
      .catch((err) => {
        // i18next falls back to the bundled English resources, so a failure
        // here is a degraded app, not a broken one. Proceeding anyway is the
        // whole point: this promise not settling used to leave the launch
        // screen up forever with nothing rendered behind it.
        console.warn('[i18n] init failed; continuing with defaults.', err);
      })
      .finally(() => setI18nReady(true));
  }, []);

  return (
    <GestureHandlerRootView
      onLayout={hideNativeSplash}
      /*
        Opaque, in the current theme. Android's window background is a single
        static colour with no dark variant — `expo.android.backgroundColor`
        (#FCFDFF) becomes `activityBackground`, which becomes
        `android:windowBackground` — so anything that briefly uncovers it shows
        near-white. In the dark theme that read as a white band sliding in from
        the bottom as the keyboard opened. The window cannot be made
        theme-aware from app config, so the fix is to never let it show.
      */
      style={[
        baseStyles.flex,
        { backgroundColor: rootColors.background },
      ]}>
      {/*
        The app is edge-to-edge (translucent status + navigation bars, with
        insets handled by SafeAreaView). Tell KeyboardProvider so it does NOT
        add its own status/nav-bar padding on top of ours — otherwise every
        screen gets a doubled top+bottom margin on Android.
      */}
      <KeyboardProvider statusBarTranslucent navigationBarTranslucent>
        <SafeAreaProvider>
          {/*
            Mounted before i18n has resolved, not after. The providers hold no
            translated state, and hoisting them means the wait below renders
            the real launch screen rather than a bare background — one picture
            from the native splash through to the first real route, instead of
            a blank frame in the middle of it.
          */}
            {/*
              Keyed on the EFFECTIVE text scale, which remounts the tree when
              the reader changes their OS text size while the app is running.

              Why a remount is the fix. Text measurement is cached per shadow
              node against layout CONSTRAINTS, and changing the text size does
              not change the screen width — so on a live change the cached size
              is returned and the new one never measured. Observed on device
              at the largest setting: chips and empty states rendered clipped,
              then corrected themselves the moment anything forced fresh nodes.
              Tapping a filter fixed that strip; signing out fixed everything.
              A remount is that same correction, applied on purpose.

              EFFECTIVE, not raw. `fontScale` runs to 3.571 but every Text is
              clamped to MAX_FONT_SCALE by the RN patch, so a move from 3.143
              to 3.571 changes nothing on screen. Keying on the raw value would
              throw the tree away for no visible difference. `Math.min` keys on
              what is actually rendered, which is six distinct states between
              0.823 and 1.2.

              The two numbers are the same number: RCTAccessibilityManager's
              multiplier table (RCTAccessibilityManager.mm:259) is identical to
              the one RCTFontSizeMultiplier() uses for the native layout
              context, so this `fontScale` IS the multiplier the renderer sees.

              COST, stated rather than hidden: a remount resets navigation. It
              is bounded by how rare the trigger is — changing text size means
              leaving for Settings and coming back — and a reader who has just
              returned to the app is the least disrupted by landing at the
              start. A clipped interface is the worse trade.
            */}
          <AppThemeProvider>
            {i18nReady ? (
              <RootLayoutContent key={effectiveFontScale} />
            ) : (
              <SplashView />
            )}
          </AppThemeProvider>
        </SafeAreaProvider>
      </KeyboardProvider>
    </GestureHandlerRootView>
  );
}

const baseStyles = StyleSheet.create({
  flex: {
    flex: 1,
  },
});
